import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Download, Receipt, ArrowRight } from "lucide-react";

function formatINR(amount) {
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(amount);
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
        return iso;
    }
}

export default function Billing() {
    const [txns, setTxns] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/billing/transactions")
            .then(({ data }) => setTxns(data))
            .catch(() => setTxns([]))
            .finally(() => setLoading(false));
    }, []);

    const totalSpent = txns.reduce((sum, t) => sum + (t.amount || 0), 0);
    const totalCredits = txns.reduce((sum, t) => sum + (t.credits || 0), 0);

    return (
        <div className="max-w-6xl mx-auto px-6 py-12" data-testid="billing-page">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
                <div>
                    <div className="overline mb-2">Billing</div>
                    <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
                        Invoices &amp; receipts<span className="text-accent">.</span>
                    </h1>
                    <p className="text-zinc-500 mt-2">Download GST-compliant invoices for every purchase.</p>
                </div>
                <Link to="/pricing" className="btn-ghost" data-testid="billing-buy-more">
                    Buy more credits <ArrowRight className="w-4 h-4" />
                </Link>
            </div>

            {/* Summary stat row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
                <div className="surface p-6">
                    <div className="overline mb-2">Total spent</div>
                    <div className="font-heading font-bold text-4xl tracking-tighter text-zinc-100">₹{formatINR(totalSpent)}</div>
                    <span className="text-xs text-zinc-500 mt-2 inline-block">Across {txns.length} purchase{txns.length === 1 ? "" : "s"}</span>
                </div>
                <div className="surface p-6">
                    <div className="overline mb-2">Credits purchased</div>
                    <div className="font-heading font-bold text-4xl tracking-tighter text-zinc-100">{totalCredits}</div>
                    <span className="text-xs text-zinc-500 mt-2 inline-block">Lifetime</span>
                </div>
            </div>

            {/* Transactions table */}
            <div className="surface overflow-hidden" data-testid="billing-table">
                <div className="p-6 border-b border-white/5">
                    <h2 className="font-heading text-xl font-semibold tracking-tight">Transaction history</h2>
                </div>

                {loading ? (
                    <div className="p-12 text-center font-mono text-sm text-zinc-500" data-testid="billing-loading">Loading...</div>
                ) : txns.length === 0 ? (
                    <div className="p-12 text-center" data-testid="billing-empty">
                        <Receipt className="w-10 h-10 mx-auto mb-4 text-zinc-600" strokeWidth={1.5} />
                        <div className="font-heading text-xl font-semibold tracking-tight mb-2">No purchases yet</div>
                        <p className="text-zinc-500 mb-6">Your invoices will appear here once you make a purchase.</p>
                        <Link to="/pricing" className="btn-primary inline-flex">
                            View pricing <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-zinc-500 border-b border-white/5">
                                <tr>
                                    <th className="text-left px-6 py-3 overline font-normal">Invoice #</th>
                                    <th className="text-left px-6 py-3 overline font-normal">Date</th>
                                    <th className="text-left px-6 py-3 overline font-normal">Package</th>
                                    <th className="text-right px-6 py-3 overline font-normal">Amount</th>
                                    <th className="text-right px-6 py-3 overline font-normal">Credits</th>
                                    <th className="text-right px-6 py-3 overline font-normal">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {txns.map((t) => (
                                    <tr key={t.id} className="hover:bg-white/[0.02]" data-testid={`billing-row-${t.order_id}`}>
                                        <td className="px-6 py-4 font-mono text-xs text-zinc-300">{t.invoice_number || "—"}</td>
                                        <td className="px-6 py-4 text-zinc-300">{formatDate(t.applied_at || t.created_at)}</td>
                                        <td className="px-6 py-4 text-zinc-200 font-medium">{t.package_name}</td>
                                        <td className="px-6 py-4 text-right text-zinc-100 font-medium">₹{formatINR(t.amount)}</td>
                                        <td className="px-6 py-4 text-right text-accent font-mono">+{t.credits}</td>
                                        <td className="px-6 py-4 text-right">
                                            <Link
                                                to={`/billing/invoice/${t.order_id}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/10 bg-surface-2 text-xs text-zinc-300 hover:text-accent hover:border-accent/40 transition-colors"
                                                data-testid={`billing-invoice-${t.order_id}`}
                                            >
                                                <Download className="w-3 h-3" /> Invoice
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <p className="mt-6 font-mono text-xs text-zinc-600">
                All amounts in INR. Invoices are numbered sequentially per Indian fiscal year (Apr–Mar).
            </p>
        </div>
    );
}
